import { useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { PaymentProviderThresholdDto } from 'api/PaymentProvider/Types/PaymentProviderThresholdDto'
import AddButton from 'components/AddButton/AddButton'
import ClientAreaAuditLogModal from 'components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { calculatePageIndex, calculateSkipIndex } from 'features/DataGrid/helpers'
import { useAppDispatch, useAppSelector } from 'store'
import { pageableSettings } from 'store/grid/grid.slice'
import { getCurrenciesAction } from 'store/trading-accounts/trading-accounts.actions'
import { fetchPaymentProviderAction } from 'store/transactions/transaction.action'
import { useApiClient } from 'utils/api/ApiClient'
import { PaymentApiClient } from 'utils/api/paymentApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from 'utils/api/useFetchOneWithFetchingUpdate'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import {
  TableStateContext,
  TableStateContextAction,
  TableStateContextData,
} from 'utils/TableStateContext'

import {
  AddWithdrawalThresholdForm,
  AddWithdrawalThresholdFormValues,
} from './AddWithdrawalThresholdForm'
import {
  EditWithdrawalThresholdForm,
  EditWithdrawalThresholdFormValues,
} from './EditWithdrawalThresholdForm'
import withdrawalThresholdsColumns from './WithdrawalThresholdsColumns'

const useMapStateToProps = () => ({
  currencies: useAppSelector((state) => state.tradingAccounts.currencies),
  paymentProviders: useAppSelector((state) => state.transactions.paymentProviders),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const paymentApiClient = useApiClient(PaymentApiClient)
  return {
    fetchCurrencies: async () => dispatch(getCurrenciesAction(tradingAccountsApiClient)),
    fetchPaymentProviders: async () => dispatch(fetchPaymentProviderAction(paymentApiClient)),
  }
}

interface WithdrawalThresholdsFetchProps {
  data: PageData<PaymentProviderThresholdDto[]> | undefined
  tableData: TableStateContextData
  setTableData: TableStateContextAction
  pageIndex: number
  pageSize: number
  isLoading: boolean
  forceRefresh(): void
  pagination: Page

  setPagination(page: Page | ((x: Page) => Page)): void
  handlePageChange(event: GridPageChangeEvent): void
}

const useWithdrawalThresholdsFetch = (): WithdrawalThresholdsFetchProps => {
  const walletsApiClient = useApiClient(WalletsApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())

  const { callback, forceRefresh } = useCallbackWithForceRefresh(
    () => {
      return walletsApiClient.getPaymentProviderThresholds({
        pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
        pageSize: paginationState.take,
        skipCount: tableData?.skipCount,
      })
    },
    [paginationState, tableData?.skipCount] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const { data, isLoading } = useFetchOneWithFetchingUpdate(callback)

  useEffect(() => {
    setTableData((props) => {
      return { ...props, skipCount: tableData?.skipCount, meta: data }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.pageIndex, data?.pageSize, data?.totalResults, tableData?.skipCount])

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPaginationState(event.page)
  }

  return {
    data,
    tableData,
    setTableData,
    pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
    pageSize: paginationState.take ?? 10,
    isLoading,
    forceRefresh,
    pagination: paginationState,
    setPagination: setPaginationState,
    handlePageChange,
  }
}

const useTablePager = (props: WithdrawalThresholdsFetchProps) => {
  const { pagination, setPagination, setTableData } = props

  const handlePageChangePager = (pagerIndex: number) => {
    setPagination((state) => {
      return { ...state, skip: calculateSkipIndex(pagerIndex, pagination.take) }
    })
  }

  const handleSizeChangePager = (take: number) => {
    setPagination((state) => {
      return { ...state, skip: 0, take }
    })
  }

  const handleSkipCountChangePager = (skipCount: boolean) => {
    setTableData((state) => {
      return { ...state, skipCount }
    })
  }

  return { handlePageChangePager, handleSizeChangePager, handleSkipCountChangePager }
}

interface ModalProps {
  data: PaymentProviderThresholdDto | undefined
  visible: boolean
}

const initialModalState = () => ({
  data: undefined,
  visible: false,
})

const WithdrawalThresholdsPage = (): JSX.Element => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)

  const { currencies, paymentProviders } = useMapStateToProps()
  const { fetchCurrencies, fetchPaymentProviders } = useMapDispatchToProps()

  const withdrawalThresholds = useWithdrawalThresholdsFetch()
  const tablePager = useTablePager(withdrawalThresholds)

  const { handlePageChangePager, handleSizeChangePager, handleSkipCountChangePager } = tablePager
  const { data, isLoading, pagination, tableData, pageIndex, pageSize } = withdrawalThresholds
  const { forceRefresh, handlePageChange, setTableData } = withdrawalThresholds

  const [isAddModalOpen, setAddModalOpen] = useState(false)
  const [editModal, setEditModal] = useState<ModalProps>(initialModalState())
  const [deleteThresholdModal, setDeleteThresholdModal] = useState<ModalProps>(initialModalState())
  const [auditLogModal, setAuditLogModal] = useState<ModalProps>(initialModalState())

  const permissions = auth?.permissions || []

  useEffect(() => {
    if (isEmpty(currencies)) {
      fetchCurrencies()
    }
    if (isEmpty(paymentProviders)) {
      fetchPaymentProviders()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditCancel = () => {
    setEditModal(initialModalState())
  }

  const handleAddCancel = () => {
    setAddModalOpen(false)
  }

  const handleConfirmationCancel = () => {
    setDeleteThresholdModal(initialModalState())
  }

  const handleAddThreshold = async (values: AddWithdrawalThresholdFormValues) => {
    try {
      await walletsApiClient.addPaymentProviderThreshold({
        paymentProviderMethodId: values.paymentProviderMethodId,
        currencyId: values.currencyId,
        bankCode: values.bankCode.trim() || null,
        amount: values.amount,
      })
      setToast(successToast('Threshold added successfully.'))
      setAddModalOpen(false)
      forceRefresh()
    } catch (error) {
      console.error(error)
    }
  }

  const handleEditThreshold = async (values: EditWithdrawalThresholdFormValues) => {
    try {
      await walletsApiClient.updatePaymentProviderThreshold(values.id, { amount: values.amount })
      setToast(successToast('Threshold updated successfully.'))
      setEditModal(initialModalState())
      forceRefresh()
    } catch (error) {
      console.error(error)
    }
  }

  const handleConfirmationDelete = async () => {
    if (deleteThresholdModal.data) {
      try {
        await walletsApiClient.deletePaymentProviderThreshold(deleteThresholdModal.data.id)
        setToast(successToast('Threshold deleted successfully.'))
      } catch (error) {
        console.error(error)
      } finally {
        handleConfirmationCancel()
        forceRefresh()
      }
    }
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <>
      <Breadcrumbs
        crumbs={[
          {
            title: 'Withdrawal Thresholds',
            link: links.administrationWithdrawalThresholds.url,
          },
        ]}
      />
      {hasPermission(permissions, PermissionsEnum.CanAddPaymentProviderThresholds) && (
        <AddButton
          text='Add new threshold'
          onClick={() => setAddModalOpen(true)}
          data-test='add-new-withdrawal-threshold-button'
        />
      )}
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal(initialModalState())}
          paymentProviderThresholdId={auditLogModal.data.id}
        />
      )}
      {deleteThresholdModal.data && (
        <ConfirmationModal
          contentText='Are you sure you want to delete the threshold configuration?'
          yesButtonText='Yes, delete'
          onYes={handleConfirmationDelete}
          onCancel={handleConfirmationCancel}
          visible={deleteThresholdModal.visible}
        />
      )}
      {isAddModalOpen && (
        <Modal visible={isAddModalOpen} onClose={handleAddCancel}>
          <AddWithdrawalThresholdForm
            paymentProviders={paymentProviders}
            currencies={currencies}
            onSubmit={handleAddThreshold}
          />
        </Modal>
      )}
      {editModal.visible && (
        <Modal visible={editModal.visible} onClose={handleEditCancel}>
          <EditWithdrawalThresholdForm
            data={editModal.data as PaymentProviderThresholdDto}
            onSubmit={handleEditThreshold}
          />
        </Modal>
      )}
      {data && (
        <TableStateContext.Provider value={[tableData, setTableData]}>
          <Grid
            resizable
            data={data.items}
            take={pagination.take}
            skip={pagination.skip}
            total={data.totalResults}
            onPageChange={handlePageChange}
            pageable={pageableSettings}
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChangePager}
                onPageChange={handlePageChangePager}
                onPageSizeChange={handleSizeChangePager}
                page={pageIndex}
                pageSize={pageSize}
                take={pagination.take || 10}
                skip={pagination.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {withdrawalThresholdsColumns
              .filter((c) => c.show !== false)
              .map((column, idx) => (
                <Column key={idx} {...column} />
              ))}
            <Column
              width={50}
              title=''
              cell={(props: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Edit',
                        'data-test': 'edit',
                        disabled: !hasPermission(
                          permissions,
                          PermissionsEnum.CanEditPaymentProviderThresholds
                        ),
                        onClick: () =>
                          setEditModal({
                            visible: true,
                            data: props.dataItem,
                          }),
                      },
                      {
                        name: 'Delete',
                        'data-test': 'delete',
                        disabled: !hasPermission(
                          permissions,
                          PermissionsEnum.CanDeletePaymentProviderThresholds
                        ),
                        onClick: () =>
                          setDeleteThresholdModal({
                            visible: true,
                            data: props.dataItem,
                          }),
                      },
                      {
                        name: 'Audit Log',
                        'data-test': 'audit-log',
                        onClick: () =>
                          setAuditLogModal({
                            visible: true,
                            data: props.dataItem,
                          }),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
              filterable={false}
            />
          </Grid>
        </TableStateContext.Provider>
      )}
    </>
  )
}

export default WithdrawalThresholdsPage
